/* ============================================================================
   ao-ui.js — Interaktionen der AO-Komponenten-Bibliothek (Welle K, 2026-07-18)
   Vanilla JS, keine Abhängigkeit von der Prototyp-Runtime. Progressive
   Enhancement + A11y (ARIA + Tastatur). Datenattribut-gesteuert, damit
   Sektionen die Komponenten nur mit Markup + Attributen einbinden.

   Hooks (Container-Attribut):
     [data-ao-accordion]  FAQ-Accordion, single-open.
                          Items: .ao-acc-item > (button.ao-acc-item__head[aria-controls]
                          + .ao-acc-item__panel[id]); Symbol .ao-acc-item__sym (+/−, von JS gesetzt).
                          Start offen: aria-expanded="true" am Head.
     [data-ao-tabs]       Kategorie-Tabs (FAQ). Tabs: [role="tab"][aria-controls]; Panels:
                          [role="tabpanel"][id]. Pfeil-/Home-/End-Navigation, Roving-Tabindex.
                          Beim Wechsel werden Accordions im gezeigten Panel geschlossen.
                          Start-Tab: aria-selected="true" (sonst der erste).
     [data-ao-toggle]     Generischer n-Wege-Umschalter (Kontakt↔Reklamation, Wurzelnackt↔Topf,
                          Rechtstext-Switcher). Buttons: [data-ao-toggle-btn][aria-controls];
                          Panels per id. Aktiv = aria-selected="true" + .is-active.
                          Start: aria-selected="true" (sonst der erste).
     [data-ao-toc]        Sticky-TOC. Links a[href="#id"] → .is-active/aria-current, sobald
                          das Ziel im Viewport ist (IntersectionObserver).
     [data-ao-consent]    „Cookie-Einstellungen" im Footer. Öffnet Shopifys Einwilligungs-Dialog
                          erneut (privacyBanner.showPreferences). Der Knopf steht `hidden` im
                          Markup und wird erst eingeblendet, wenn die API wirklich da ist.

   Ohne JS: Panels ohne `hidden`-Attribut authoren → alles sichtbar/bedienbar;
   ao-ui.js übernimmt die Zustände beim Init.
   ========================================================================== */
(function () {
  'use strict';

  var uidN = 0;
  function uid(prefix) { uidN += 1; return prefix + '-' + uidN; }

  function panelFor(el) {
    var id = el.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  function setAccItem(head, panel, open) {
    head.setAttribute('aria-expanded', open ? 'true' : 'false');
    panel.hidden = !open;
    var sym = head.querySelector('.ao-acc-item__sym');
    if (sym) sym.textContent = open ? '−' : '+'; /* − / + */
  }

  function collapseAccordions(scope) {
    if (!scope) return;
    scope.querySelectorAll('.ao-acc-item').forEach(function (item) {
      var h = item.querySelector('.ao-acc-item__head');
      var p = item.querySelector('.ao-acc-item__panel');
      if (h && p) setAccItem(h, p, false);
    });
  }

  /* --- FAQ-Accordion (single-open) ---------------------------------------- */
  function initAccordion(root) {
    if (root.dataset.aoInit === '1') return;
    root.dataset.aoInit = '1';

    var items = Array.prototype.slice.call(root.querySelectorAll('.ao-acc-item'));
    if (!items.length) return;

    items.forEach(function (item) {
      var head = item.querySelector('.ao-acc-item__head');
      var panel = item.querySelector('.ao-acc-item__panel');
      if (!head || !panel) return;

      if (!panel.id) panel.id = uid('ao-acc-panel');
      head.setAttribute('aria-controls', panel.id);
      if (head.tagName === 'BUTTON' && !head.hasAttribute('type')) head.setAttribute('type', 'button');

      setAccItem(head, panel, head.getAttribute('aria-expanded') === 'true');

      head.addEventListener('click', function () {
        var willOpen = head.getAttribute('aria-expanded') !== 'true';
        items.forEach(function (other) {
          var oh = other.querySelector('.ao-acc-item__head');
          var op = other.querySelector('.ao-acc-item__panel');
          if (oh && op) setAccItem(oh, op, other === item ? willOpen : false);
        });
      });
    });
  }

  /* --- Kategorie-Tabs (role=tab, single-select) --------------------------- */
  function initTabs(root) {
    if (root.dataset.aoInit === '1') return;
    root.dataset.aoInit = '1';

    var tabs = Array.prototype.slice.call(root.querySelectorAll('[role="tab"]'));
    if (!tabs.length) return;

    function select(tab, focus) {
      tabs.forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.classList.toggle('is-active', on);
        t.tabIndex = on ? 0 : -1;
        var panel = panelFor(t);
        if (panel) {
          panel.hidden = !on;
          if (on) collapseAccordions(panel); /* Spec: Tabwechsel schließt Accordion */
        }
      });
      if (focus) tab.focus();
    }

    tabs.forEach(function (tab) {
      if (tab.tagName === 'BUTTON' && !tab.hasAttribute('type')) tab.setAttribute('type', 'button');
      tab.addEventListener('click', function () { select(tab, false); });
      tab.addEventListener('keydown', function (e) {
        var i = tabs.indexOf(tab), n = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = (i + 1) % tabs.length;
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') n = 0;
        else if (e.key === 'End') n = tabs.length - 1;
        if (n !== null) { e.preventDefault(); select(tabs[n], true); }
      });
    });

    var initial = tabs.filter(function (t) { return t.getAttribute('aria-selected') === 'true'; })[0] || tabs[0];
    select(initial, false);
  }

  /* --- Generischer Toggle (n-Wege) ---------------------------------------- */
  function initToggle(root) {
    if (root.dataset.aoInit === '1') return;
    root.dataset.aoInit = '1';

    var btns = Array.prototype.slice.call(root.querySelectorAll('[data-ao-toggle-btn]'));
    if (!btns.length) return;

    function activate(btn) {
      btns.forEach(function (b) {
        var on = b === btn;
        b.setAttribute('aria-selected', on ? 'true' : 'false');
        b.classList.toggle('is-active', on);
        var panel = panelFor(b);
        if (panel) panel.hidden = !on;
      });
    }

    btns.forEach(function (b) {
      if (b.tagName === 'BUTTON' && !b.hasAttribute('type')) b.setAttribute('type', 'button');
      b.addEventListener('click', function () { activate(b); });
    });

    var initial = btns.filter(function (b) { return b.getAttribute('aria-selected') === 'true'; })[0] || btns[0];
    activate(initial);
  }

  /* --- Sticky-TOC-Aktiv-Marker -------------------------------------------- */
  function initToc(root) {
    if (root.dataset.aoInit === '1') return;
    root.dataset.aoInit = '1';

    if (!('IntersectionObserver' in window)) return;
    var links = Array.prototype.slice.call(root.querySelectorAll('a[href^="#"]'));
    var map = new Map();
    links.forEach(function (a) {
      var id = a.getAttribute('href').slice(1);
      var target = id && document.getElementById(id);
      if (target) map.set(target, a);
    });
    if (!map.size) return;

    var obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        links.forEach(function (l) { l.classList.remove('is-active'); l.removeAttribute('aria-current'); });
        var a = map.get(e.target);
        if (a) { a.classList.add('is-active'); a.setAttribute('aria-current', 'true'); }
      });
    }, { rootMargin: '0px 0px -70% 0px', threshold: 0 });

    map.forEach(function (a, target) { obs.observe(target); });
  }

  /* --- PDP Live-Summe („In den Warenkorb · {Summe}") --------------------- */
  function initPdpLiveTotal(root) {
    if (root.dataset.aoTotalInit === '1') return;
    var totalEl = root.querySelector('[data-ao-atc-total]');
    var priceEl = root.querySelector('.ao-pdp__price .product-price--original');
    var qtyEl = root.querySelector('.ao-pdp__cart-row .qty-selector');
    if (!totalEl || !priceEl || !qtyEl) return;
    /* Ausverkauft: keine Summe in den Kaufbutton schreiben (28.08.2026).
       Der Mengenwaehler ist bei fehlender Verfuegbarkeit nur `display: none`; das
       <input name="quantity" value="1"> steht weiter im DOM. Der Guard oben prueft
       nur, OB die Elemente da sind, nicht ob das Produkt kaufbar ist — die Summe
       wurde also auch hinter das Wort "Ausverkauft" geschrieben. Im Browser gemessen
       auf /products/aronia-trinkglas: button.innerText = "Ausverkauft · 1,95 €".
       Betraf am 28.08. alle 9 ausverkauften der 44 aktiven Artikel. */
    var aoAtcBtn = root.querySelector('.add-to-cart');
    if (aoAtcBtn && (aoAtcBtn.disabled || aoAtcBtn.classList.contains('disabled'))) {
      totalEl.textContent = '';
      return;
    }
    root.dataset.aoTotalInit = '1';
    function parseEuro(t) {
      var s = (t || '').replace(/[^0-9.,]/g, '').replace(/\./g, '').replace(',', '.');
      return parseFloat(s) || 0;
    }
    /* Waehrung aus dem aktiven Markt statt hartem "€" (05.08.2026).
       Hier stand `n.toFixed(2).replace('.', ',') + ' €'`. Der Shop hat zwei Maerkte
       (Deutschland primaer + Europaeische Union mit localCurrencies: true), die
       EU-Variante rechnet EUR in die Landeswaehrung um. Aus einer schwedischen
       Session gemessen: Seite wies 79,00 kr aus, der Kaufbutton daneben 79,00 €.
       Die ZAHL war richtig — Liquid liefert die Betraege in der Marktwaehrung —,
       nur das Zeichen war fest verdrahtet. Faellt Intl aus, greift das alte Format. */
    var aoWaehrung = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
    var aoGeld = null;
    try {
      aoGeld = new Intl.NumberFormat(document.documentElement.lang || 'de-DE',
        { style: 'currency', currency: aoWaehrung });
    } catch (e) { aoGeld = null; }
    function fmt(n) {
      return aoGeld ? aoGeld.format(n) : n.toFixed(2).replace('.', ',') + ' €';
    }

    /* Abo-Preise (05.08.2026): Waehlt der Kunde im Subscriptions-Widget ein Intervall,
       gilt nicht mehr der Einmalkauf-Preis, sondern per_delivery_price des Selling Plans
       (7,5–12,5 % Rabatt). Ohne das zeigte der Button weiter den vollen Preis — bei
       2 × 0,7-l-Saft im 8-Wochen-Abo 13,98 € statt 12,94 €.
       Die Zuordnung Plan-ID -> Cent liefert das Liquid als data-Attribut am Anker. */
    var aboEl = root.querySelector('[data-ao-abo-preise]');
    var aboPreise = {};
    if (aboEl) {
      try { aboPreise = JSON.parse(aboEl.getAttribute('data-ao-abo-preise') || '{}'); }
      catch (e) { aboPreise = {}; }
    }
    function einzelpreis() {
      var plan = root.querySelector('input[name="selling_plan"]');
      var id = plan && plan.value;
      if (id && aboPreise[id] != null) return aboPreise[id] / 100;
      return parseEuro(priceEl.textContent);
    }

    /* Abo-Preiszeile unter dem Preis (30.08.2026). Bis hierher zeigte die Seite oben
       weiter den Einmalkauf-Preis, waehrend der Kaufbutton daneben schon den Abo-Preis
       trug — gemessen 23,99 € gegen 21,11 € auf einem Bildschirm. Der Einmalkauf-Preis
       bleibt stehen (beide Betraege nebeneinander ist die ehrlichere Anzeige, und
       `einzelpreis()` liest ihn aus genau diesem Element), daneben tritt die Abo-Zeile.
       Kein selbst errechneter Rabattsatz: Der steht bereits in der Plan-Bezeichnung
       der App. */
    var aboHinweis = root.querySelector('[data-ao-abo-hinweis]');
    function aboZeile(unit) {
      if (!aboHinweis) return;
      var plan = root.querySelector('input[name="selling_plan"]');
      var id = plan && plan.value;
      if (id && aboPreise[id] != null && unit > 0) {
        var aboZeileTmpl = (window.AO_I18N && window.AO_I18N['ao.ui.subscription_price_line']) || 'Im Abo {{amount}} pro Lieferung';
        aboHinweis.textContent = aboZeileTmpl.replace('{{amount}}', fmt(unit));
        aboHinweis.hidden = false;
      } else {
        aboHinweis.textContent = '';
        aboHinweis.hidden = true;
      }
    }

    /* Preis in der dauerhaften Kaufleiste. Bewusst der EINZELpreis, nicht die Summe:
       Er soll dasselbe sagen wie die Preisangabe oben, sonst stehen zwei verschiedene
       Zahlen fuer dasselbe Produkt auf einem Bildschirm. */
    var stickyPreis = document.querySelector('[data-ao-sticky-preis]');

    function update() {
      var unit = einzelpreis();
      var qty = parseInt(qtyEl.value, 10) || 1;
      totalEl.textContent = unit > 0 ? ' · ' + fmt(unit * qty) : '';
      aboZeile(unit);
      if (stickyPreis) stickyPreis.textContent = unit > 0 ? fmt(unit) : '';
    }
    update();
    qtyEl.addEventListener('change', update);
    qtyEl.addEventListener('input', update);
    root.querySelectorAll('.ao-pdp__cart-row .qty-minus, .ao-pdp__cart-row .qty-plus').forEach(function (b) {
      b.addEventListener('click', function () { setTimeout(update, 30); });
    });
    /* Delegation + Verzoegerung: Die App rendert ihre Radios erst nach dem Mount und setzt
       den hidden `selling_plan` asynchron, nachdem das change-Event durch ist. */
    if (aboEl) {
      aboEl.addEventListener('change', function () { setTimeout(update, 60); });
      aboEl.addEventListener('click', function () { setTimeout(update, 120); });
    }
  }

  /* --- Dauerhafte Kaufleiste auf der Produktseite (30.08.2026) --------------
     Erscheint, sobald der echte Kaufbereich den Bildschirm verlaesst. Der Knopf
     delegiert an den echten Kaufknopf im Formular — kein zweites Formular, weil der
     Krown-Commerce-Vertrag byte-treu bleiben muss (die erste Fassung dieser Seite ist
     genau daran gescheitert). Ausverkauft wird gespiegelt statt neu entschieden. */
  function initPdpStickyBar(root) {
    var leiste = document.querySelector('[data-ao-sticky]');
    if (!leiste || leiste.dataset.aoInit === '1') return;
    var echterKnopf = root.querySelector('.add-to-cart');
    var kaufBereich = root.querySelector('.ao-pdp__cart-row');
    if (!echterKnopf || !kaufBereich) return;
    leiste.dataset.aoInit = '1';

    var knopf = leiste.querySelector('[data-ao-sticky-knopf]');

    function spiegeln() {
      var aus = echterKnopf.disabled || echterKnopf.classList.contains('disabled');
      knopf.disabled = aus;
      knopf.classList.toggle('is-disabled', aus);
    }
    spiegeln();
    new MutationObserver(spiegeln).observe(echterKnopf, { attributes: true, attributeFilter: ['disabled', 'class'] });

    knopf.addEventListener('click', function () {
      if (knopf.disabled) return;
      echterKnopf.click();
    });

    /* `hidden` statt nur Transform: So ist die Leiste, solange der echte Kaufknopf
       sichtbar ist, auch fuer Screenreader und die Tabreihenfolge weg — sonst gaebe es
       zwei erreichbare Kaufknoepfe fuer dieselbe Handlung. */
    if (!('IntersectionObserver' in window)) return;
    new IntersectionObserver(function (eintraege) {
      var sichtbar = eintraege[0].isIntersecting;
      if (sichtbar) {
        leiste.classList.remove('is-an');
        leiste.hidden = true;
      } else {
        leiste.hidden = false;
        requestAnimationFrame(function () { leiste.classList.add('is-an'); });
      }
    }, { rootMargin: '0px 0px -20px 0px' }).observe(kaufBereich);
  }

  /* --- PLP Header-Sort: navigiert mit sort_by, bewahrt aktive Filter ------ */
  function initPlpSort(sel) {
    if (sel.dataset.aoInit === '1') return;
    sel.dataset.aoInit = '1';
    sel.addEventListener('change', function () {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', sel.value);
      window.location.assign(url.toString());
    });
  }

  /* --- PLP Kollektion: client-seitiges Filtern/Sortieren (Kollektion.dc.html) --
     Reproduziert den DCLogic-State des Prototyps: Filter (Kategorie/Preis-Buckets/
     Eigenschaften/Nur-verfügbar), Chips, Reset, Custom-Sort-Dropdown, Mobile-Toggle.
     Alle Produkte einer Kollektion (<=39) sind auf einer Seite → reines Show/Hide +
     DOM-Reorder, kein Server-Refetch. Commerce (ATC/quick-buy) bleibt unberührt.    */
  function initAoCollection(root) {
    if (root.dataset.aoInit === '1') return;
    root.dataset.aoInit = '1';

    var grid = root.querySelector('[data-ao-plp-grid]');
    if (!grid) return;
    var cards = Array.prototype.slice.call(grid.querySelectorAll('[data-ao-card]'));
    cards.forEach(function (c, i) { c.dataset.pop = i; });

    var countEl = root.querySelector('[data-ao-plp-count]');
    var chipsEl = root.querySelector('[data-ao-plp-chips]');
    var emptyEl = root.querySelector('[data-ao-plp-empty]');

    var buckets = { 'u7': [0, 700], '7-15': [700, 1500], '15-30': [1500, 3000], 'o30': [3000, Infinity] };
    var priceLabels = {
      'u7': (window.AO_I18N && window.AO_I18N['ao.ui.price_under_7']) || 'bis 7 €',
      '7-15': (window.AO_I18N && window.AO_I18N['ao.ui.price_7_to_15']) || '7 – 15 €',
      '15-30': (window.AO_I18N && window.AO_I18N['ao.ui.price_15_to_30']) || '15 – 30 €',
      'o30': (window.AO_I18N && window.AO_I18N['ao.ui.price_over_30']) || 'über 30 €'
    };

    function inputs(f) { return Array.prototype.slice.call(root.querySelectorAll('[data-ao-filter="' + f + '"]')); }
    var catInputs = inputs('kat'), eigInputs = inputs('eig'), priceInputs = inputs('price');
    var availInput = root.querySelector('[data-ao-filter="avail"]');

    function chip(label, onRemove) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'ao-plp__chip';
      b.appendChild(document.createTextNode(label));
      var x = document.createElement('span');
      x.className = 'ao-plp__chipx'; x.setAttribute('aria-hidden', 'true'); x.textContent = '✕';
      b.appendChild(x);
      var removeFilterTmpl = (window.AO_I18N && window.AO_I18N['ao.ui.remove_filter']) || 'Filter entfernen: {{label}}';
      b.setAttribute('aria-label', removeFilterTmpl.replace('{{label}}', label));
      b.addEventListener('click', onRemove);
      return b;
    }

    function renderChips(priceSel, onlyAvail) {
      if (!chipsEl) return;
      chipsEl.textContent = '';
      catInputs.forEach(function (i) { if (i.checked) chipsEl.appendChild(chip(i.value, function () { i.checked = false; apply(); })); });
      eigInputs.forEach(function (i) { if (i.checked) chipsEl.appendChild(chip(i.value, function () { i.checked = false; apply(); })); });
      if (priceSel) chipsEl.appendChild(chip(priceLabels[priceSel.value] || priceSel.value, function () { priceSel.checked = false; apply(); }));
      if (onlyAvail) chipsEl.appendChild(chip((window.AO_I18N && window.AO_I18N['ao.ui.only_available']) || 'Nur verfügbare', function () { availInput.checked = false; apply(); }));
    }

    function apply() {
      var cats = catInputs.filter(function (i) { return i.checked; }).map(function (i) { return i.value; });
      var eigs = eigInputs.filter(function (i) { return i.checked; }).map(function (i) { return i.value; });
      var priceSel = priceInputs.filter(function (i) { return i.checked; })[0];
      var onlyAvail = availInput && availInput.checked;
      var visible = 0;
      cards.forEach(function (card) {
        var kat = (card.dataset.kat || '').split('|').filter(Boolean);
        var eig = (card.dataset.eig || '').split('|').filter(Boolean);
        var price = parseInt(card.dataset.price || '0', 10);
        var avail = card.dataset.available === 'true';
        var ok = true;
        if (cats.length && !cats.some(function (c) { return kat.indexOf(c) > -1; })) ok = false;
        if (ok && eigs.length && !eigs.some(function (x) { return eig.indexOf(x) > -1; })) ok = false;
        if (ok && priceSel) { var b = buckets[priceSel.value]; if (!(price >= b[0] && price < b[1])) ok = false; }
        if (ok && onlyAvail && !avail) ok = false;
        card.style.display = ok ? '' : 'none';
        if (ok) visible++;
      });
      if (countEl) {
        var produktEinzahl = (window.AO_I18N && window.AO_I18N['ao.ui.count_product_one']) || ' Produkt';
        var produktMehrzahl = (window.AO_I18N && window.AO_I18N['ao.ui.count_product_other']) || ' Produkte';
        countEl.textContent = visible + (visible === 1 ? produktEinzahl : produktMehrzahl);
      }
      if (emptyEl) emptyEl.hidden = visible !== 0;
      renderChips(priceSel, onlyAvail);
    }

    function sortBy(mode) {
      cards.slice().sort(function (a, b) {
        if (mode === 'preis-auf') return (+a.dataset.price) - (+b.dataset.price);
        if (mode === 'preis-ab') return (+b.dataset.price) - (+a.dataset.price);
        if (mode === 'neu') return (+b.dataset.published) - (+a.dataset.published);
        return (+a.dataset.pop) - (+b.dataset.pop);
      }).forEach(function (c) { grid.appendChild(c); });
    }

    catInputs.concat(eigInputs).concat(priceInputs).forEach(function (i) { i.addEventListener('change', apply); });
    if (availInput) availInput.addEventListener('change', apply);

    root.querySelectorAll('[data-ao-plp-reset]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        catInputs.concat(eigInputs).concat(priceInputs).forEach(function (i) { i.checked = false; });
        if (availInput) availInput.checked = false;
        apply();
      });
    });

    var sortBox = root.querySelector('[data-ao-plp-sortbox]');
    if (sortBox) {
      var sortBtn = sortBox.querySelector('[data-ao-plp-sortbtn]');
      var sortPanel = sortBox.querySelector('[data-ao-plp-sortpanel]');
      var sortCurrent = sortBox.querySelector('[data-ao-plp-sortcurrent]');
      var opts = Array.prototype.slice.call(sortBox.querySelectorAll('[data-ao-plp-sortopt]'));
      var openSort = function (open) { sortPanel.hidden = !open; sortBtn.setAttribute('aria-expanded', open ? 'true' : 'false'); };
      sortBtn.addEventListener('click', function (e) { e.stopPropagation(); openSort(sortPanel.hidden); });
      opts.forEach(function (o) {
        o.addEventListener('click', function () {
          opts.forEach(function (x) { x.classList.remove('is-active'); });
          o.classList.add('is-active');
          if (sortCurrent) sortCurrent.textContent = o.textContent;
          sortBy(o.getAttribute('data-ao-plp-sortopt'));
          openSort(false);
        });
      });
      document.addEventListener('click', function (e) { if (!sortBox.contains(e.target)) openSort(false); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape') openSort(false); });
    }

    var mToggle = root.querySelector('[data-ao-plp-mobiletoggle]');
    var sidebar = root.querySelector('[data-ao-plp-sidebar]');
    if (mToggle && sidebar) {
      mToggle.addEventListener('click', function () {
        var open = sidebar.classList.toggle('is-open');
        mToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }

    apply();
  }

  /* --- Lesefortschritt (News-Artikel) ------------------------------------- */
  function initReadingProgress(bar) {
    if (bar.dataset.aoInit === '1') return;
    bar.dataset.aoInit = '1';
    var on = function () {
      var h = document.scrollingElement || document.documentElement;
      var max = h.scrollHeight - h.clientHeight;
      bar.style.width = (max > 0 ? Math.min(100, (h.scrollTop / max) * 100) : 0) + '%';
    };
    window.addEventListener('scroll', on, { passive: true });
    window.addEventListener('resize', on, { passive: true });
    on();
  }

  /* --- Cookie-Einstellungen (Widerruf der Einwilligung) --------------------
     Art. 7 Abs. 3 DSGVO: Der Widerruf muss so einfach sein wie die Erteilung.
     Ohne diesen Knopf ist Shopifys Banner nach der ersten Entscheidung nicht
     mehr erreichbar.

     Warum nicht der fertige Link aus shop.policies? Der zeigt auf
     `/policies/#shopifyReshowConsentBanner`, und `/policies/` ist in diesem Shop
     ein 404 — der Besucher landete in einer Sackgasse. Der dokumentierte Weg ist
     privacyBanner.showPreferences().
     -------------------------------------------------------------------------- */
  function initConsentLink(btn) {
    if (btn.dataset.aoInit === '1') return;
    btn.dataset.aoInit = '1';

    function bereit() {
      return window.privacyBanner && typeof window.privacyBanner.showPreferences === 'function';
    }

    btn.addEventListener('click', function () {
      if (bereit()) window.privacyBanner.showPreferences();
    });

    /* Shopifys Banner-Skript laedt asynchron; eine einmalige Pruefung beim Init
       kaeme regelmaessig zu frueh. Deshalb kurz nachfassen — und den Knopf erst
       zeigen, wenn er auch etwas bewirkt. Ein toter Knopf waere schlimmer als
       gar keiner. */
    if (bereit()) { btn.hidden = false; return; }
    var wartezeit = 0;
    var timer = setInterval(function () {
      wartezeit += 250;
      if (bereit()) { clearInterval(timer); btn.hidden = false; }
      else if (wartezeit >= 8000) { clearInterval(timer); }
    }, 250);
  }

  /* --- Bootstrap ---------------------------------------------------------- */
  function initAll(scope) {
    var ctx = scope || document;
    ctx.querySelectorAll('[data-ao-accordion]').forEach(initAccordion);
    ctx.querySelectorAll('[data-ao-tabs]').forEach(initTabs);
    ctx.querySelectorAll('[data-ao-toggle]').forEach(initToggle);
    ctx.querySelectorAll('[data-ao-toc]').forEach(initToc);
    ctx.querySelectorAll('.ao-pdp').forEach(initPdpLiveTotal);
    ctx.querySelectorAll('.ao-pdp').forEach(initPdpStickyBar);
    ctx.querySelectorAll('[data-ao-plp-sort]').forEach(initPlpSort);
    ctx.querySelectorAll('[data-ao-plp]').forEach(initAoCollection);
    ctx.querySelectorAll('[data-ao-progress]').forEach(initReadingProgress);
    ctx.querySelectorAll('[data-ao-consent]').forEach(initConsentLink);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* Shopify-Theme-Editor: neu geladene Sektionen nachrüsten. */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });

  /* Öffentlicher Re-Init-Hook (z. B. nach AJAX-Nachladen). */
  window.aoUI = { init: initAll };
})();
